import React, { useEffect, useState } from "react";
import {
    Link,
    NavLink,
    Outlet,
    useLocation,
    useNavigate,
    useParams,
} from "react-router-dom";
import {
    FolderKanban,
    Beaker,
    LineChart,
    Settings,
    Wallet,
    LogOut,
    User as UserIcon,
    ChevronRight,
} from "lucide-react";

import Wordmark from "@/components/site/Wordmark";
import RegionPicker from "@/components/site/RegionPicker";
import { authService } from "@/services/authService";
import { useAuth, useTier } from "@/contexts/AuthContext";

const SIDEBAR_GROUPS = [
    {
        title: "Workspace",
        items: [
            { to: "/app/projects", label: "Projects", Icon: FolderKanban, end: false },
            { to: "/app/library/materials", label: "Materials library", Icon: Beaker },
            { to: "/app/library/calibrations", label: "Calibrations", Icon: LineChart },
        ],
    },
    {
        title: "Account",
        items: [
            { to: "/app/account", label: "Settings", Icon: Settings },
            { to: "/app/account?tab=billing", label: "Billing", Icon: Wallet },
        ],
    },
];

export default function AppShell() {
    const { account, signOut } = useAuth();
    const { tier } = useTier();
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();
    const [menuOpen, setMenuOpen] = useState(false);
    const [projectName, setProjectName] = useState(null);

    // Lightweight breadcrumb: load project name when inside a project route.
    useEffect(() => {
        const match = location.pathname.match(/^\/app\/projects\/([^/]+)/);
        if (!match) {
            setProjectName(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await authService.http.get(`/projects/${match[1]}`);
                if (!cancelled) setProjectName(data.name);
            } catch {
                if (!cancelled) setProjectName(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [location.pathname]);

    const onSignOut = async () => {
        await signOut();
        navigate("/login", { replace: true });
    };

    return (
        <div
            data-testid="app-shell"
            className="min-h-screen flex flex-col bg-[#f6f7f6] text-[#0e1416]"
        >
            {/* Top bar */}
            <header className="sticky top-0 z-40 bg-[#0e1416] text-[#e6ebec] border-b border-[#1a2124]">
                <div className="px-5 lg:px-7 h-[56px] flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Link
                            to="/app"
                            data-testid="app-wordmark-link"
                            className="flex items-baseline"
                        >
                            <Wordmark variant="dark" size="text-[16px]" />
                        </Link>
                        <span className="tc-mono text-[10px] text-[#5a6669] border-l border-[#1a2124] pl-3">
                            workspace
                        </span>
                        {projectName && (
                            <div className="hidden sm:flex items-center gap-2 tc-mono text-[11px] text-[#8a9498] ml-2">
                                <ChevronRight size={12} strokeWidth={1.5} />
                                <span
                                    data-testid="app-breadcrumb-project"
                                    className="text-[#e6ebec]"
                                >
                                    {projectName}
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="relative">
                        <button
                            type="button"
                            data-testid="user-menu-toggle"
                            onClick={() => setMenuOpen((v) => !v)}
                            className="inline-flex items-center gap-3 px-3 py-1.5 hover:bg-[#1a2124] transition-colors"
                        >
                            <div className="w-6 h-6 rounded-full bg-[#1a2124] inline-flex items-center justify-center">
                                <UserIcon size={13} strokeWidth={1.5} className="text-[#8a9498]" />
                            </div>
                            <div className="hidden sm:flex flex-col items-start leading-tight">
                                <span className="text-[12px] text-[#e6ebec]">
                                    {account?.email}
                                </span>
                                <span className="tc-mono text-[9.5px] text-[#01696f] tracking-wider uppercase">
                                    {tier}
                                </span>
                            </div>
                        </button>
                        {menuOpen && (
                            <div
                                data-testid="user-menu-panel"
                                className="absolute right-0 top-full mt-1 w-[240px] bg-[#0e1416] border border-[#1a2124] shadow-xl"
                            >
                                <Link
                                    to="/app/account"
                                    onClick={() => setMenuOpen(false)}
                                    data-testid="user-menu-account"
                                    className="block px-4 py-2.5 text-[12px] text-[#e6ebec] hover:bg-[#1a2124] transition-colors"
                                >
                                    Account
                                </Link>
                                <Link
                                    to="/billing"
                                    onClick={() => setMenuOpen(false)}
                                    data-testid="user-menu-upgrade"
                                    className="block px-4 py-2.5 text-[12px] text-[#01696f] hover:bg-[#1a2124] transition-colors"
                                >
                                    {tier === "professional" ? "Manage billing" : "Upgrade →"}
                                </Link>
                                <Link
                                    to="/pricing"
                                    onClick={() => setMenuOpen(false)}
                                    className="block px-4 py-2.5 text-[12px] text-[#e6ebec] hover:bg-[#1a2124] transition-colors"
                                >
                                    Plans
                                </Link>
                                <div
                                    data-testid="user-menu-region-row"
                                    className="border-t border-[#1a2124] px-4 py-2.5 flex items-center justify-between gap-3"
                                >
                                    <span className="tc-mono text-[10px] text-[#5a6669] uppercase tracking-wider">
                                        Region
                                    </span>
                                    <RegionPicker placement="nav" />
                                </div>
                                <button
                                    type="button"
                                    onClick={onSignOut}
                                    data-testid="user-menu-signout"
                                    className="w-full text-left px-4 py-2.5 text-[12px] text-[#e6ebec] hover:bg-[#1a2124] border-t border-[#1a2124] transition-colors inline-flex items-center gap-2"
                                >
                                    <LogOut size={12} strokeWidth={1.5} />
                                    Sign out
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </header>

            <div className="flex-1 flex">
                {/* Sidebar */}
                <aside
                    data-testid="app-sidebar"
                    className="hidden md:flex flex-col w-[228px] shrink-0 border-r border-[#d8dcdd] bg-[#f6f7f6]"
                >
                    <nav className="flex flex-col gap-6 px-5 py-7">
                        {SIDEBAR_GROUPS.map((group) => (
                            <div key={group.title} className="flex flex-col gap-2">
                                <div className="tc-eyebrow text-[#8a9498]">
                                    {group.title}
                                </div>
                                <ul className="flex flex-col gap-0.5">
                                    {group.items.map((item) => (
                                        <li key={item.to}>
                                            <NavLink
                                                to={item.to}
                                                data-testid={`sidebar-link-${item.label
                                                    .toLowerCase()
                                                    .replace(/\s+/g, "-")}`}
                                                end={item.end ?? true}
                                                className={({ isActive }) =>
                                                    `flex items-center gap-2 px-2 py-1.5 text-[13px] transition-colors ${
                                                        isActive
                                                            ? "text-[#01696f]"
                                                            : "text-[#5a6669] hover:text-[#0e1416]"
                                                    }`
                                                }
                                            >
                                                <item.Icon
                                                    size={14}
                                                    strokeWidth={1.5}
                                                />
                                                {item.label}
                                            </NavLink>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </nav>

                    <div className="mt-auto px-5 py-5 border-t border-[#d8dcdd] tc-mono text-[10px] text-[#5a6669] leading-[1.5]">
                        Concrete Control · v0.2 · Phase 2A<br />
                        www.concrete-control.com
                    </div>
                </aside>

                {/* Main */}
                <main className="flex-1 min-w-0">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
